import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import CountdownTimer from "./countdown-timer";
import { getSATime } from "@shared/timezone";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthState";
import { apiRequest } from "@/lib/queryClient";
import { 
  Trophy, 
  Share2, 
  Play, 
  Gamepad2, 
  DollarSign, 
  Dumbbell, 
  MapPin,
  Gift,
  Heart,
  Zap,
  Network,
  Globe,
  Edit,
  BarChart3,
  Users,
  TrendingUp
} from "lucide-react";

interface CompetitionCardProps {
  competition: {
    id: number;
    title: string;
    description: string;
    shareText?: string;
    prize?: string; // Legacy field for backward compatibility
    prize1?: string;
    prize2?: string;
    prize3?: string;
    prizeValue?: number;
    startDate: string;
    startTime?: string;
    endDate: string;
    endTime?: string;
    type: string;
    category: string;
    daysOfWeek?: string[];
    bannerImageUrl?: string;
    termsAndConditionsDialog?: string;
    isActive: boolean;
    hasEntered?: boolean;
  };
}

interface ReferralData {
  referralCode: string;
  referralUrl: string;
}

interface CompetitionStats {
  participantCount: number;
  totalReferrals: number;
  verifiedReferrals: number;
  pendingReferrals: number;
  leaderboard: Array<{
    userId: number;
    gamifiedName: string;
    progressCount: number;
    lastUpdated: string;
  }>;
}

// Form schema for editing competition
const editCompetitionSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  prize1: z.string().optional(),
  prize2: z.string().optional(),
  prize3: z.string().optional(),
  startDate: z.string().min(1, "Start date is required"),
  startTime: z.string().optional(),
  endDate: z.string().optional(),
  endTime: z.string().optional(),
  type: z.string().min(1, "Type is required"),
  category: z.string().min(1, "Category is required"),
  shareText: z.string().optional(),
  termsAndConditionsDialog: z.string().optional(),
  isActive: z.boolean().optional()
});

type EditCompetitionData = z.infer<typeof editCompetitionSchema>;

export default function CompetitionCard({ competition }: CompetitionCardProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isLoggedIn = !!user;
  const isAdmin = user?.role === 'admin';
  
  // State for dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  
  // Form for editing competition
  const editForm = useForm<EditCompetitionData>({
    resolver: zodResolver(editCompetitionSchema),
    defaultValues: {
      title: competition.title,
      description: competition.description || '',
      prize1: competition.prize1 || '',
      prize2: competition.prize2 || '',
      prize3: competition.prize3 || '',
      startDate: new Date(competition.startDate).toISOString().split('T')[0],
      startTime: competition.startTime || '',
      endDate: competition.endDate ? new Date(competition.endDate).toISOString().split('T')[0] : '',
      endTime: competition.endTime || '',
      type: competition.type,
      category: competition.category,
      shareText: competition.shareText || '',
      termsAndConditionsDialog: competition.termsAndConditionsDialog || '',
      isActive: competition.isActive
    }
  });
  
  // Competition analytics query
  const { data: competitionStats, isLoading: statsLoading, error: statsError } = useQuery<CompetitionStats>({
    queryKey: [`/api/admin/competitions/${competition.id}/stats`],
    enabled: isAdmin && analyticsDialogOpen
  });
  
  // Edit competition mutation
  const editMutation = useMutation({
    mutationFn: async (data: EditCompetitionData) => {
      const response = await apiRequest('PUT', `/api/competitions/${competition.id}`, { body: data });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Competition updated successfully"
      });
      setEditDialogOpen(false);
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["/api/competitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/featured-competitions"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update competition",
        variant: "destructive"
      });
    }
  });
  
  const handleEditSubmit = (data: EditCompetitionData) => {
    editMutation.mutate(data);
  };
  
  // Trust the backend's isActive status first, then calculate local timing for display
  const now = new Date();
  
  // Create proper start datetime by parsing the date part and combining with time
  const startDateOnly = new Date(competition.startDate).toISOString().split('T')[0]; // Get YYYY-MM-DD
  const startTimeStr = competition.startTime || '00:00';
  const startDateTime = new Date(`${startDateOnly}T${startTimeStr}:00+02:00`); // SA timezone
  
  // Create proper end datetime by parsing the date part and combining with time  
  const endDateOnly = new Date(competition.endDate).toISOString().split('T')[0]; // Get YYYY-MM-DD
  const endTimeStr = competition.endTime || '23:59';
  const endDateTime = new Date(`${endDateOnly}T${endTimeStr}:59+02:00`); // SA timezone
  
  // Use backend's isActive status as the primary source of truth
  const isExpired = !competition.isActive;
  const isScheduled = competition.isActive && startDateTime > now;
  
  // Fetch user's referral data for sharing
  const { data: referralData } = useQuery<ReferralData>({
    queryKey: ["/api/referral-code"],
  });
  
  // Get the competition type to display
  const getDisplayType = () => {
    // Return the simple competition type based on the title
    if (competition.title.toLowerCase().includes('daily') || competition.title.toLowerCase().includes('day')) {
      return 'DAILY';
    }
    if (competition.title.toLowerCase().includes('weekly') || competition.title.toLowerCase().includes('week')) {
      return 'WEEKLY';
    }
    if (competition.title.toLowerCase().includes('monthly') || competition.title.toLowerCase().includes('month')) {
      return 'MONTHLY';
    }
    // Default to WEEKLY
    return 'WEEKLY';
  };

  // Handle click for scheduled competitions
  const handleScheduledClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startTime = competition.startTime || '00:00';
    const startDate = new Date(competition.startDate).toLocaleDateString();
    toast({
      title: "Competition Not Started",
      description: `This competition starts on ${startDate} at ${startTime}. Please check back then!`,
      variant: "default"
    });
  };

  // Build the card content JSX
  const cardContent = (
    <Card className={`hover:shadow-xl transition-all duration-300 overflow-hidden bg-white rounded-lg relative ${
      !isExpired && !isScheduled ? 'hover:scale-[1.02] cursor-pointer' : ''
    } ${competition.hasEntered ? "opacity-75" : ""} ${
      isScheduled ? 'opacity-70' : ''
    }`} 
    data-testid="competition-card">
      {/* Admin buttons positioned inside the card */}
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-2 z-10">
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/90 hover:bg-white border-gray-300"
                data-testid="edit-competition-button"
                onClick={(e) => e.stopPropagation()}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Competition</DialogTitle>
              </DialogHeader>
              <Form {...editForm}>
                <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4">
                  <FormField
                    control={editForm.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="edit-title-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} data-testid="edit-description-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={editForm.control}
                      name="prize1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prize 1</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="edit-prize1-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="prize2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prize 2</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="edit-prize2-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="prize3"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prize 3</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="edit-prize3-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={editForm.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="edit-start-date-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="startTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} data-testid="edit-start-time-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={editForm.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="edit-end-date-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="endTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Time</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} data-testid="edit-end-time-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={editForm.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="edit-type-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={editForm.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <FormControl>
                            <Input {...field} data-testid="edit-category-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={editForm.control}
                    name="shareText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Share Text</FormLabel>
                        <FormControl>
                          <Textarea {...field} data-testid="edit-share-text-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="termsAndConditionsDialog"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Competition-Specific Terms & Conditions (Optional)</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="Enter specific terms and conditions for this competition. Users will see this in a popup dialogue before entering." 
                            className="min-h-[100px]"
                            data-testid="edit-terms-dialog-input" 
                          />
                        </FormControl>
                        <div className="text-sm text-muted-foreground">
                          Optional: Add competition-specific terms that will appear in a popup dialogue. If left blank, only the global terms will be shown.
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Competition Active</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Toggle to activate or deactivate this competition
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="edit-active-switch"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditDialogOpen(false)}
                      data-testid="edit-cancel-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={editMutation.isPending}
                      data-testid="edit-save-button"
                    >
                      {editMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/90 hover:bg-white border-gray-300"
                data-testid="analytics-competition-button"
                onClick={(e) => e.stopPropagation()}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Competition Analytics</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {statsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="text-gray-500">Loading analytics...</div>
                  </div>
                ) : statsError ? (
                  <div className="text-center py-8 text-red-500">
                    Error loading analytics: {statsError.message}
                  </div>
                ) : competitionStats ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-600">Participants</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-900" data-testid="analytics-participants">
                          {competitionStats.participantCount}
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-600">Total Referrals</span>
                        </div>
                        <div className="text-2xl font-bold text-green-900" data-testid="analytics-total-referrals">
                          {competitionStats.totalReferrals}
                        </div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-5 w-5 text-purple-600" />
                          <span className="text-sm font-medium text-purple-600">Verified</span>
                        </div>
                        <div className="text-2xl font-bold text-purple-900" data-testid="analytics-verified-referrals">
                          {competitionStats.verifiedReferrals}
                        </div>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Share2 className="h-5 w-5 text-orange-600" />
                          <span className="text-sm font-medium text-orange-600">Pending</span>
                        </div>
                        <div className="text-2xl font-bold text-orange-900" data-testid="analytics-pending-referrals">
                          {competitionStats.pendingReferrals}
                        </div>
                      </div>
                    </div>
                    
                    {competitionStats.leaderboard && competitionStats.leaderboard.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Top Performers</h3>
                        <div className="space-y-2" data-testid="analytics-leaderboard">
                          {competitionStats.leaderboard.map((entry, index) => (
                            <div key={entry.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                  {index + 1}
                                </div>
                                <span className="font-medium">{entry.gamifiedName}</span>
                              </div>
                              <div className="text-lg font-bold text-blue-600">
                                {entry.progressCount} referrals
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No analytics data available
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      
      <CardContent className="p-4 sm:p-6">
        {/* Day of Week Badge and Timer - Side by Side */}
        <div className="flex items-center justify-between mb-4">
          <Badge className="bg-blue-500 text-white text-xs px-3 py-1 rounded-full uppercase font-medium">
            {getDisplayType()}
          </Badge>
          
          {/* Smaller Countdown Timer - Only shown for active and scheduled competitions */}
          {!isExpired && (
            <div className="flex gap-1">
              {isScheduled ? (
                <CountdownTimer endDate={competition.startDate} endTime={competition.startTime} isActive={competition.isActive} compact />
              ) : (
                <CountdownTimer endDate={competition.endDate} endTime={competition.endTime} isActive={competition.isActive} compact />
              )}
            </div>
          )}
        </div>

        {/* Title with Winner Count */}
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
          {competition.title}
        </h3>
        
        {/* Description */}
        <p className="text-gray-600 mb-4 text-sm">
          {competition.shareText || "Join this exciting competition for your chance to win amazing prizes!"}
        </p>

        {/* Prize Section - Stacked Vertically */}
        <div className="mb-6">
          <div className="text-sm font-semibold text-gray-900 mb-2">PRIZE</div>
          <div className="space-y-2">
            {competition.prize1 && (
              <div className="flex items-start">
                <span className="text-sm font-medium text-gray-900">{competition.prize1}</span>
              </div>
            )}
            {competition.prize2 && (
              <div className="flex items-start">
                <span className="text-sm font-medium text-gray-900">{competition.prize2}</span>
              </div>
            )}
            {competition.prize3 && (
              <div className="flex items-start">
                <span className="text-sm font-medium text-gray-900">{competition.prize3}</span>
              </div>
            )}
            {/* Fallback for backward compatibility with old prize field */}
            {!competition.prize1 && !competition.prize2 && !competition.prize3 && competition.prize && (
              <div className="flex items-start">
                <span className="text-sm font-medium text-gray-900">{competition.prize}</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms & Conditions Apply Section */}
        <div className="mb-4 text-center">
          <Link to="/terms" onClick={(e) => e.stopPropagation()} className="text-xs text-gray-500 hover:text-blue-600 underline transition-colors">
            Ts & Cs Apply
          </Link>
        </div>

        {/* Enter Now Button - Full Width */}
        {!isExpired && (
          <>
            {isScheduled ? (
              // Scheduled competitions show popup instead of navigating
              <Button 
                onClick={handleScheduledClick}
                className="w-full bg-blue-900 hover:bg-blue-950 text-white font-semibold py-3 rounded-lg text-sm uppercase tracking-wide"
                data-testid="enter-now-button"
              >
                COMING SOON
              </Button>
            ) : !isLoggedIn ? (
              <Link to={`/auth?tab=signup&redirect=/competition/${competition.id}&comp=${competition.id}`} onClick={(e) => e.stopPropagation()}>
                <Button 
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg text-sm uppercase tracking-wide"
                  data-testid="enter-now-button"
                >
                  ENTER NOW
                </Button>
              </Link>
            ) : (
              <Link to={`/competition/${competition.id}#enter-competition`} onClick={(e) => e.stopPropagation()}>
                <Button 
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg text-sm uppercase tracking-wide"
                  data-testid="enter-now-button"
                >
                  {competition.hasEntered ? "ENTERED" : "ENTER NOW"}
                </Button>
              </Link>
            )}
          </>
        )}
        
        {isExpired && (
          <Button 
            className="w-full bg-gray-400 text-white font-semibold py-3 rounded-lg text-sm uppercase tracking-wide"
            disabled
            data-testid="competition-ended-button"
          >
            COMPETITION ENDED
          </Button>
        )}
      </CardContent>
    </Card>
  );

  // Return logic based on competition state
  if (isScheduled) {
    // For scheduled competitions, don't wrap in Link - show popup instead
    return (
      <div 
        data-testid="competition-card-link"
        onClick={handleScheduledClick}
        className="cursor-pointer relative"
      >
        {cardContent}
      </div>
    );
  }

  if (isExpired) {
    // For expired competitions, render directly without link
    return (
      <div className="relative" data-testid="competition-card-link">
        {cardContent}
      </div>
    );
  }

  // For active competitions, wrap in Link component
  const linkDestination = isLoggedIn ? `/competition/${competition.id}#enter-competition` : "/auth";
  
  return (
    <div className="relative">
      <Link 
        to={linkDestination}
        data-testid="competition-card-link"
        onClick={(e) => {
          // Prevent link navigation if user clicks the admin buttons
          if ((e.target as HTMLElement).closest('[data-testid="edit-competition-button"]') || 
              (e.target as HTMLElement).closest('[data-testid="analytics-competition-button"]')) {
            e.preventDefault();
            return;
          }
          // Prevent link navigation if user clicks the Enter Now button
          if ((e.target as HTMLElement).closest('[data-testid="enter-now-button"]')) {
            e.preventDefault();
            return;
          }
          // Handle mobile scrolling for logged in users
          if (isLoggedIn && window.innerWidth < 768) {
            e.preventDefault();
            window.location.href = `/competition/${competition.id}#enter-competition`;
          }
        }}
      >
        {cardContent}
      </Link>
    </div>
  );
}